import { useMutation, useQuery, useQueryClient } from 'react-query';
import type {
  CreateServiceChargeRequest,
  UpdateServiceChargeRequest,
  GetServiceChargesParams,
  GetDailySummaryParams,
  GetMonthlyReportParams,
  GetDateRangeReportParams,
  GetTopPerformersParams
} from '../services/serviceChargeService';
import { message } from 'antd';
import { serviceChargeService } from '../services/serviceChargeService';
import { employeeService, type GetEmployeesParams } from '../services/employeeService';
import { serviceBonusService, type GetServiceBonusParams } from '../services/serviceBonusService';

// Query Keys
export const serviceChargeKeys = {
  all: ['service-charges'] as const,
  lists: () => [...serviceChargeKeys.all, 'list'] as const,
  list: (params: GetServiceChargesParams) => [...serviceChargeKeys.lists(), params] as const,
  details: () => [...serviceChargeKeys.all, 'detail'] as const,
  detail: (id: string) => [...serviceChargeKeys.details(), id] as const,
  reports: () => [...serviceChargeKeys.all, 'reports'] as const,
  dailySummary: (params: GetDailySummaryParams) => 
    [...serviceChargeKeys.reports(), 'daily', params] as const,
  monthlyReport: (params: GetMonthlyReportParams) => 
    [...serviceChargeKeys.reports(), 'monthly', params] as const,
  dateRangeReport: (params: GetDateRangeReportParams) => 
    [...serviceChargeKeys.reports(), 'date-range', params] as const,
  topPerformers: (params: GetTopPerformersParams) => 
    [...serviceChargeKeys.reports(), 'top-performers', params] as const,
};

export const userKeys = {
  // Level 1: Root key - ระดับบนสุด
  all: ['users'] as const,
  
  // Level 2: List category - ประเภทของ list
  lists: () => [...userKeys.all, 'list'] as const,
  
  // Level 3: Specific list with params - list เฉพาะพร้อม parameters
  list: (params: GetEmployeesParams) => [...userKeys.lists(), params] as const,
};

export const serviceBonusKeys = {
  // Level 1: Root key - ระดับบนสุด
  all: ['serviceBonus'] as const,
  
  // Level 2: List category - ประเภทของ list
  lists: () => [...serviceBonusKeys.all, 'list'] as const,
  
  // Level 3: Specific list with params - list เฉพาะพร้อม parameters
  list: (params: GetEmployeesParams) => [...serviceBonusKeys.lists(), params] as const,
};
// ==================== QUERIES ====================

// Get users list
export const useUsers = (params: GetEmployeesParams = {}) => {
  return useQuery({
    queryKey: userKeys.list(params),
    queryFn: () => employeeService.getEmployees(params),
    staleTime: 30000, // 30 seconds
  });
};

// Get users list
export const useServiceBonuses = (params: GetServiceBonusParams = {}) => {
  return useQuery({
    queryKey: serviceBonusKeys.list(params),
    queryFn: () => serviceBonusService.getServiceBonuses(params),
    staleTime: 30000, // 30 seconds
  });
};

// Get service charges list
export const useServiceCharges = (params: GetServiceChargesParams = {}) => {
  return useQuery({
    queryKey: serviceChargeKeys.list(params),
    queryFn: () => serviceChargeService.getServiceCharges(params),
    staleTime: 30000, // 30 seconds
  });
};

// Get service charge by ID
export const useServiceCharge = (id: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: serviceChargeKeys.detail(id),
    queryFn: () => serviceChargeService.getServiceCharge(id),
    enabled: !!id && enabled,
  });
};

// Get today's summary
export const useTodaySummary = () => {
  const today = new Date().toISOString().split('T')[0];
  return useQuery({
    queryKey: serviceChargeKeys.dailySummary({ date: today }),
    queryFn: () => serviceChargeService.getTodaySummary(),
    staleTime: 60000, // 1 minute
    refetchInterval: 300000, // refetch every 5 minutes
  });
};

// Get monthly report
export const useMonthlyReport = (year: number, month: number) => {
  return useQuery({
    queryKey: serviceChargeKeys.monthlyReport({ year, month }),
    queryFn: () => serviceChargeService.getMonthlyReport({ year, month }),
    staleTime: 300000, // 5 minutes
  });
};

// Get top performers
export const useTopPerformers = (params: GetTopPerformersParams) => {
  return useQuery({
    queryKey: serviceChargeKeys.topPerformers(params),
    queryFn: () => serviceChargeService.getTopPerformers(params),
    enabled: !!params.startDate && !!params.endDate,
    staleTime: 300000, // 5 minutes
  });
};

// ==================== MUTATIONS ====================

// Create service charge
export const useCreateServiceCharge = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateServiceChargeRequest) => 
      serviceChargeService.createServiceCharge(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: serviceChargeKeys.lists() });
      queryClient.invalidateQueries({ queryKey: serviceChargeKeys.reports() });
      message.success('ສ້າງຂໍ້ມູນສຳເລັດ');
    },
    onError: (error: any) => {
      if (error.response?.data?.errors) {
        error.response.data.errors.forEach((err: any) => {
          message.error(err.message);
        });
      } else {
        message.error(error.response?.data?.message || 'ເກີດຂໍ້ຜິດພາດ');
      }
    },
  });
};

// Update service charge
export const useUpdateServiceCharge = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateServiceChargeRequest }) =>
      serviceChargeService.updateServiceCharge(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: serviceChargeKeys.lists() });
      queryClient.invalidateQueries({ queryKey: serviceChargeKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: serviceChargeKeys.reports() });
      message.success('ແກ້ໄຂຂໍ້ມູນສຳເລັດ');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'ເກີດຂໍ້ຜິດພາດ');
    },
  });
};

// Delete service charge
export const useDeleteServiceCharge = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => serviceChargeService.deleteServiceCharge(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: serviceChargeKeys.lists() });
      queryClient.invalidateQueries({ queryKey: serviceChargeKeys.reports() });
      message.success('ລຶບຂໍ້ມູນສຳເລັດ');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'ເກີດຂໍ້ຜິດພາດ');
    },
  });
};

// Restore service charge
export const useRestoreServiceCharge = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => serviceChargeService.restoreServiceCharge(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: serviceChargeKeys.lists() });
      queryClient.invalidateQueries({ queryKey: serviceChargeKeys.reports() });
      message.success('ກູ້ຄືນຂໍ້ມູນສຳເລັດ');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'ເກີດຂໍ້ຜິດພາດ');
    },
  });
};